/* global routes, publish, PUB_SUB_EVENTS */
(() => {
  if (customElements.get('cart-drawer-shipping-protection')) return;

  class CartDrawerShippingProtection extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-cart-drawer-shipping-protection-toggle]');
      this.errorBox = this.querySelector('[role="alert"]');
      this.onToggle = this.handleToggle.bind(this);
      this.toggle?.addEventListener('click', this.onToggle);
    }

    disconnectedCallback() {
      this.toggle?.removeEventListener('click', this.onToggle);
    }

    getSectionsToRender() {
      if (window.CartOperations) return window.CartOperations.sections();
      const sections = [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
      // The drawer can also be opened over the full cart page.
      for (const id of ['main-cart-items', 'main-cart-footer']) {
        const element = document.getElementById(id);
        if (element) sections.push({ id, section: element.dataset.id, selector: '.js-contents' });
      }
      return sections;
    }

    async request(url, options = {}) {
      const response = await fetch(url, options);
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || window.cartStrings.error);
      }
      return data;
    }

    updateCart(updates, sections) {
      return this.request(`${routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates,
          sections: sections.map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });
    }

    async handleToggle() {
      if (this.busy || this.toggle?.disabled || !this.dataset.variantId) return;

      this.busy = true;
      const enable = this.toggle.getAttribute('aria-checked') !== 'true';
      const update = () => this.updateProtection(enable);
      return window.CartOperations ? window.CartOperations.run(update) : update();
    }

    async updateProtection(enable) {
      const restoreFocus = document.activeElement === this.toggle;
      const checkoutButtons = [...document.querySelectorAll('#CartDrawer-Checkout, #checkout')]
        .filter((button) => !button.disabled);
      this.busy = true;
      this.toggle.disabled = true;
      checkoutButtons.forEach((button) => { button.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      this.errorBox.textContent = '';

      try {
        const sections = this.getSectionsToRender();
        // Set an absolute quantity so even a stale drawer cannot add a second unit.
        let data = await this.updateCart({ [this.dataset.variantId]: enable ? 1 : 0 }, sections);
        const lines = data.items.filter((item) => String(item.variant_id) === this.dataset.variantId);
        const updates = {};
        // Shopify updates only the first matching variant. Remove any separate
        // coverage lines using fresh keys returned by the write, without a GET.
        lines.forEach((line, index) => {
          if (!enable || index > 0) updates[line.key] = 0;
        });
        if (Object.keys(updates).length) data = await this.updateCart(updates, sections);
        if (window.CartOperations) data = await window.CartOperations.removeOrphanShipping(data);
        this.renderSections(data, sections, restoreFocus);
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: data, cartDataIsComplete: true });
        }
      } catch (error) {
        this.errorBox.textContent = error.message || window.cartStrings.error;
        if (window.CartOperations) await window.CartOperations.recover();
      } finally {
        this.busy = false;
        this.toggle.disabled = false;
        checkoutButtons.forEach((button) => { button.disabled = false; });
        this.setAttribute('aria-busy', 'false');
      }
    }

    renderSections(data, sections, restoreFocus) {
      // Validate the drawer before replacing anything: a successful cart write
      // can still return a null section. Do not silently leave an outdated total.
      const drawerHTML = data.sections?.['cart-drawer'];
      if (!drawerHTML || !new DOMParser().parseFromString(drawerHTML, 'text/html').querySelector('#CartDrawer')) {
        throw new Error('Your cart was updated. Please refresh the page to see the current total.');
      }
      const drawer = this.closest('cart-drawer');
      if (window.CartOperations) window.CartOperations.render(data);
      else for (const section of sections) {
        const html = data.sections?.[section.section];
        const container = document.getElementById(section.id);
        if (!html || !container) continue;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        if (!source) continue;
        const target = container.querySelector(section.selector) || container;
        target.innerHTML = source.innerHTML;
      }
      if (typeof data.item_count === 'number') {
        drawer?.classList.toggle('is-empty', data.item_count === 0);
        document.querySelector('cart-items')?.classList.toggle('is-empty', data.item_count === 0);
        document.getElementById('main-cart-footer')?.classList.toggle('is-empty', data.item_count === 0);
      }
      if (restoreFocus && drawer?.classList.contains('active')) {
        const toggle = drawer.querySelector('[data-cart-drawer-shipping-protection-toggle]');
        (toggle || drawer.querySelector('[data-cart-drawer-close]'))?.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('cart-drawer-shipping-protection', CartDrawerShippingProtection);
})();
